<template>
  <table class="cost-allocation-order-cost-table w-full">
    <thead class="border-bottom-1 border-color-divider">
      <tr>
        <th
          v-for="(header, index) in TABLE_HEADERS"
          :key="index"
          class="text-gray-darker uppercase"
          :class="[header.textStyle]"
        >
          {{ header.name }}
        </th>
      </tr>
    </thead>

    <tbody>
      <template v-if="shipmentIds.length > 0">
        <CostAllocationOrderCostTableRowResults
          v-for="shipmentId in shipmentIds"
          :key="shipmentId"
          :shipment-id="shipmentId"
          :shipment-rate-allocation="getRateAllocationForShipment(shipmentId)"
          :table-column-size="TABLE_HEADERS.length"
          :loading="loading"
          :billing-currency="billingCurrency"
          class="border-y-1 border-color-divider line-height-1 loading p-0"
        />
      </template>

      <tr v-else class="border-y-1 border-color-divider line-height-1">
        <td :colspan="TABLE_HEADERS.length">
          {{
            t('common.noParam', {
              param: t('shipmentFinancials.ledgerEntries', 2),
            })
          }}
        </td>
      </tr>
    </tbody>

    <tfoot
      class="font-bold text-gray-darkest border-top-1 border-color-divider"
    >
      <tr>
        <th class="text-left">
          {{ t('common.paramTotals', { param: t('orders.orders') }) }}
        </th>

        <th
          :colspan="TABLE_HEADERS.length - 1"
          class="border-x-1 border-color-divider text-right"
        >
          {{ renderMoney(orderTotalForShipments) }}
        </th>
      </tr>
    </tfoot>
  </table>
</template>

<script lang="ts" setup>
import { computed, PropType, toRefs } from 'vue';
import { useI18n } from 'vue-i18n';

import { renderMoney } from '@rygen/rygen-ui/i18n';
import { Currency, Money } from '@rygen/rygen-ui/model';
import { notNull } from '@rygen/rygen-ui/util';

import { ShipmentId, ShipmentRateAllocation } from '@/model/shipmentFinancials';

import CostAllocationOrderCostTableRowResults from '@/components/shipmentFinancials/costAllocation/CostAllocationOrderCostTableRowResults.vue';

const props = defineProps({
  shipmentIds: {
    type: Array as PropType<number[]>,
    default: () => [],
  },
  shipmentRateAllocationsByShipment: {
    type: Map as PropType<Map<ShipmentId, ShipmentRateAllocation>>,
    default: () => new Map(),
  },
  billingCurrency: {
    type: String as PropType<Currency>,
    default: () => Currency.USD,
  },
  loading: {
    type: Boolean,
    default: false,
  },
});
const {
  shipmentIds,
  shipmentRateAllocationsByShipment,
  billingCurrency,
  loading,
} = toRefs(props);

const { t } = useI18n();

const TABLE_HEADERS: Readonly<{ name: string; textStyle: string }[]> =
  Object.freeze([
    { name: t('shipmentFinancials.ledgerEntries'), textStyle: 'text-left' },
    {
      name: t('shipmentFinancials.paramEstimate', {
        param: t('shipmentFinancials.cost'),
      }),
      textStyle: 'text-right',
    },
    { name: t('shipmentFinancials.cost'), textStyle: 'text-right' },
  ]);

const getRateAllocationForShipment = (
  shipmentId: number,
): ShipmentRateAllocation | undefined => {
  if (shipmentId) {
    const sourceSystemId = shipmentId.toString();
    if (shipmentRateAllocationsByShipment.value.has(sourceSystemId)) {
      return shipmentRateAllocationsByShipment.value.get(sourceSystemId);
    }
  }

  // allocations may not exist yet for a Shipment,
  return undefined;
};

const orderTotalForShipments = computed(() => {
  return (
    Array.from(shipmentRateAllocationsByShipment.value.values())
      ?.map((rateAllocation) => {
        return rateAllocation.lineItems
          .map((lineItem) => lineItem.amount)
          .filter(notNull)
          .reduce(
            (prev: Money, cur: Money) => prev.plus(cur),
            Money.zero(billingCurrency.value),
          );
      })
      .reduce(
        (prev: Money, cur: Money) => prev.plus(cur),
        Money.zero(billingCurrency.value),
      ) ?? Money.zero(billingCurrency.value)
  );
});
</script>

<style>
.cost-allocation-order-cost-table {
  border-collapse: collapse;

  th,
  td {
    min-width: 120px;
  }

  thead {
    border-bottom: var(--divider);

    tr {
      th {
        font-size: var(--sp-3);
        font-weight: 500;
        padding: var(--sp-3);

        &:first-of-type {
          padding-left: var(--sp-6);
        }

        &:last-of-type {
          padding-right: var(--sp-6);
          border-right: none;
        }
      }
    }
  }

  tbody {
    tr {
      td {
        padding: var(--sp-3);

        &:first-of-type {
          padding-left: var(--sp-6);
        }

        &:last-of-type {
          padding-right: var(--sp-6);
          border-right: none;
        }
      }
    }
  }

  tfoot {
    tr {
      th,
      td {
        padding: var(--sp-3);
        border-right: none;

        &:first-of-type {
          padding-left: var(--sp-6);
        }

        &:last-of-type {
          padding-right: var(--sp-6);
        }
      }
    }
  }
}
</style>
